import { Box, Checkbox, Flex, HStack, Select, Spinner, Text, VStack } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";

import { G1Button } from "@components/G1Button";
import { ContactsImportList } from "@components/Import/ContactsImportList";
import { SummaryBadge } from "@components/Import/SummaryBadge";
import { ContactToImport, ImportContactStatus, ImportViewPhase } from "@components/Import/types";
import { SeaShellBox } from "@components/SeaShellBox";
import { $g1Color } from "@/theme/G1Style";

interface ContactsImportTableProps {
    filename: string;
    contacts: ContactToImport[];
    phase: ImportViewPhase;
    onClose: () => void;
    onConfirmImport: (contacts: ContactToImport[]) => void
}

export const ContactsImportTable = (props: ContactsImportTableProps) => {
    const { filename, contacts, phase, onClose, onConfirmImport } = props;
    const [includeWarnings, setIncludeWarnings] = useState(false);
    const [statusFilters, setStatusFilters] = useState<Set<ImportContactStatus>>(new Set(["ok", "warning", "excluded", "importing", "imported", "failed"]));
    const [sortByStatus, setSortByStatus] = useState(true);
    const [alphaSort, setAlphaSort] = useState<"asc" | "desc" | null>(null);
    const contactsToImport = useMemo(() => {
        const allowedStatuses = includeWarnings ? ["ok", "warning"] : ["ok"];
        return contacts.filter((c) => allowedStatuses.includes(c.status));
    }, [contacts, includeWarnings]);
    const sortedContacts = useMemo(
        () => [...contacts].sort((a, b) => {
            if (sortByStatus) {
                const statusCmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
                if (statusCmp !== 0) { return statusCmp; }
            }

            if (alphaSort) {
                let compare = a.data.lastName.trim().toLowerCase().localeCompare(b.data.lastName.trim().toLowerCase(), 'fr', { sensitivity: 'base' });
                if (compare !== 0) { return alphaSort === "asc" ? compare : -compare; }
                
                compare = a.data.firstName.trim().toLowerCase().localeCompare(b.data.firstName.trim().toLowerCase(), 'fr', { sensitivity: 'base' });
                if (compare !== 0) { return alphaSort === "asc" ? compare : -compare; }
            }
            return 0;
        }),
        [contacts, sortByStatus, alphaSort]
    );

    const total = contacts.length;
    const okCount = contacts.filter((c) => c.status === "ok").length;
    const warningCount = contacts.filter((c) => c.status === "warning").length;
    const excludedCount = contacts.filter((c) => c.status === "excluded").length;
    const importableCount = includeWarnings ? okCount + warningCount : okCount;
    const importedCount = contacts.filter((c) => c.status === "imported").length;
    const failedCount = contacts.filter((c) => c.status === "failed").length;

    const isPreview = phase === "preview";
    const isImporting = phase === "importing";
    const isDone = phase === "done";

    const toggleFilter = (status: ImportContactStatus) => {
        setStatusFilters((prev) => {
            const next = new Set(prev);
            if (next.has(status)) {
                if (isPreview && ((next.size - 3) <= 1))  { return prev; } // At least one status must be selected (3 for not showed status => inPreview phase : ["importing", "imported", "failed"] ; inDone phase : ["ok", "warning", "importing"]
                next.delete(status);
            } else {
                next.add(status);
            }
            return next;
        });
    };

    useEffect(() => {
        if (isImporting) { setSortByStatus(true); }
    }, [phase]);

    return (
        <SeaShellBox component="form" spacing={5} mt={2}>
            <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap">
                <Box>
                    <Text fontSize="lg" fontWeight="bold">
                        {isPreview && "Aperçu de l'importation"}
                        {isImporting && "Importation en cours..."}
                        {isDone && "Importation terminée"}
                    </Text>
                    <Text fontSize="sm" color="gray.400" fontWeight="normal">{filename} · {total} contacts détectés</Text>
                </Box>
                <HStack spacing={3} pr={8}>
                    {isPreview && (
                        <VStack>
                            <HStack>
                                <G1Button component="form" variant="cancel" title="Annuler" fontSize="sm" onClick={onClose} />
                                <G1Button component="form" variant="important" title={`Importer ${importableCount} contacts`} isDisabled={importableCount === 0} fontSize="sm" onClick={() => onConfirmImport(contactsToImport)} />
                            </HStack>
                            {warningCount > 0 && (
                                <Checkbox
                                    isChecked={includeWarnings}
                                    size="sm" mt={2} mb={4}
                                    sx={{
                                        '& .chakra-checkbox__control': {
                                            borderWidth: '2px',
                                            borderColor: 'black',
                                            bg: 'gray.50',
                                            _checked: {
                                                bg: 'blue.500',
                                                borderColor: 'blue.500',
                                            },
                                        },
                                    }}
                                    onChange={(e) => setIncludeWarnings(e.target.checked)}
                                >
                                    <Text fontSize="sm">
                                        Inclure, si possible, les {warningCount} contact{warningCount > 1 ? "s" : ""} en avertissement
                                    </Text>
                                </Checkbox>
                            )}
                        </VStack>
                    )}
                    {isImporting && <Spinner size="md" color="CSFBlue.500" thickness="2px" speed="1s" />}
                    {!isPreview && (
                        <G1Button component="form" variant="cancel" title="Fermer" fontSize="sm" isDisabled={isImporting} onClick={onClose} />
                    )}
                </HStack>
            </Flex>
 
            <Flex maxH="85vh" direction="column">
                <Flex wrap="wrap" align="center" columnGap={3} rowGap={2} mb={2}>
                    <Text fontSize="sm">Filtres</Text>
                    {(isPreview || isImporting) && (
                        <>
                            <SummaryBadge count={okCount} label={`importation${okCount > 1 ? 's' : ''}`} colorScheme="green"
                                          isActive={isImporting || statusFilters.has("ok")}
                                          onClick={isImporting ? undefined : () => toggleFilter("ok")}
                            />
                            <SummaryBadge count={warningCount} label={`avertissement${warningCount > 1 ? 's' : ''}`} colorScheme="orange"
                                          isActive={isImporting || statusFilters.has("warning")}
                                          onClick={isImporting ? undefined : () => toggleFilter("warning")}
                            />
                        </>
                    )}
                    {isDone && (
                        <>
                            <SummaryBadge count={importedCount} label={`importé${importedCount > 1 ? "s" : ""}`} colorScheme="green" isActive={statusFilters.has("imported")} onClick={() => toggleFilter("imported")} />
                            <SummaryBadge count={failedCount} label={`échoué${failedCount > 1 ? "s" : ""}`} colorScheme="red" isActive={statusFilters.has("failed")} onClick={() => toggleFilter("failed")} />
                        </>
                    )}
                    <SummaryBadge count={excludedCount} label={`exclu${excludedCount > 1 ? 's' : ''}`} colorScheme="red"
                                  isActive={isImporting || statusFilters.has("excluded")}
                                  onClick={isImporting ? undefined : () => toggleFilter("excluded")}
                    />
                </Flex>

                <Flex direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "center" }} gap={3}>
                    <Checkbox 
                        isChecked={sortByStatus}
                        disabled={isImporting}
                        size="sm"
                        sx={{
                            '& .chakra-checkbox__control': {
                                borderWidth: '2px',
                                borderColor: 'black',
                                bg: 'gray.50',
                                _checked: {
                                    bg: 'blue.500',
                                    borderColor: 'blue.500',
                                },
                            },
                        }}
                        onChange={(e) => setSortByStatus(e.target.checked)}>
                        <Text fontSize="sm">Trier par statut</Text>
                    </Checkbox>

                    <Text fontSize="sm" display={{ base: "none", md: "block" }}>/</Text>
                    <HStack>
                        <Text fontSize="sm">Tri alphabétique par :</Text>
                        <Select size="sm" w="auto" value={alphaSort ?? "none"} disabled={isImporting}
                                onChange={(e) => setAlphaSort(e.target.value === "none" ? null : e.target.value as "asc" | "desc")}
                        >
                            <option value="none">Aucun tri</option>
                            <option value="asc">Nom A → Z</option>
                            <option value="desc">Nom Z → A</option>
                        </Select>
                    </HStack>
                </Flex>

                {//isCompact ? (
                    <ContactsImportList contacts={isImporting ? sortedContacts : sortedContacts.filter((c) => statusFilters.has(c.status))} bgColor={$g1Color('list.contacts.bg')} />
                    //) : (
                    // <TableContainer 
                    //     border="1px solid"
                    //     borderColor={$g1Color('list.contacts.bg')}
                    //     borderRadius="lg"
                    //     overflowY="auto"
                    //     flex="1"
                    //     minH={0}
                    //     sx={{
                    //         "&::-webkit-scrollbar": { width: "8px" },
                    //         "&::-webkit-scrollbar-track": { background: "transparent" },
                    //         "&::-webkit-scrollbar-thumb": {
                    //             background: "var(--chakra-colors-gray-600)",
                    //             borderRadius: "8px",
                    //         },
                    //         "&::-webkit-scrollbar-thumb:hover": {
                    //             background: "var(--chakra-colors-gray-500)",
                    //         },
                    //     }}
                    // >
                    //     <Table variant="simple" size="sm" sx={{ borderCollapse: "collapse" }}>
                    //         <Thead bg={$g1Color('list.contacts.bg')} position="sticky" top={0}>
                    //             <Tr>
                    //                 <Th color="gray.400" borderColor="gray.400" w="8" />
                    //                 <Th color="gray.400" borderColor="gray.400" paddingInlineStart={0}>Nom</Th>
                    //                 <Th color="gray.400" borderColor="gray.400">Prénom</Th>
                    //                 <Th color="gray.400" borderColor="gray.400">Email</Th>
                    //                 <Th color="gray.400" borderColor="gray.400">Téléphone</Th>
                    //             </Tr>
                    //         </Thead>
                    //         <Tbody>
                    //             {sortedContacts.map((contact, idx) => (
                    //                 <ContactRowCells key={idx} contact={contact} backgroundColor={$g1Color('list.contacts.bg')} isLast={idx === sortedContacts.length - 1} />
                    //             ))}
                    //         </Tbody>
                    //     </Table>
                    // </TableContainer>
                /*)*/}
            </Flex>
        </SeaShellBox>
    );
}

const STATUS_ORDER: Record<ImportContactStatus, number> = {
    importing: 0,
    imported: 1,
    ok: 2,
    warning: 3,
    failed: 4,
    excluded: 5,
};